import { useEffect, useRef, useState } from 'react';
import { DocxEditorPane, type DocxFixHandle } from '../components/DocxEditorPane';
import { DocumentFixList } from '../components/DocumentFixList';
import { STUB_FIXES, type DocumentFix } from '../components/documentFixes';
import { acceptAllTrackedChanges } from '../components/docxReview';
import '../../styles/app-documents.css';

/**
 * DEV-ONLY harness for the compliance-fix overlay spike. Loads a bundled sample .docx (no backend needed) and
 * mounts the editor with the hardcoded findings, plus the reference-column fixes list with Jump / Apply. Route:
 * /dev/fixes. Lets us iterate on highlights + cards + apply without onboarding/upload.
 */
export default function DevFixesPage() {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [fixes, setFixes] = useState<DocumentFix[]>(STUB_FIXES);
  const [activeFix, setActiveFix] = useState<string | null>(null);
  const [locatedFixIds, setLocatedFixIds] = useState<Set<string>>(new Set());
  const fixHandle = useRef<DocxFixHandle>(null);

  useEffect(() => {
    let alive = true;
    // ?doc=<name> loads a different sample from /public (e.g. tracked-sample.docx); defaults to the policy.
    // ?accept=1 runs the buffer through the headless "accept all tracked changes" reviewer first.
    const params = new URLSearchParams(window.location.search);
    const doc = params.get('doc') || 'sample-policy.docx';
    const acceptFirst = params.get('accept') === '1';
    fetch(`/${doc}`)
      .then((r) => r.arrayBuffer())
      .then((b) => (acceptFirst ? acceptAllTrackedChanges(b) : b))
      .then((b) => alive && setBuffer(b))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="apage apage--wide apage--doc" style={{ paddingTop: 24 }}>
      <h1 style={{ margin: '0 0 16px', font: '700 24px/1.2 system-ui' }}>Compliance fixes (dev harness)</h1>
      <div className="docs-split docs-split--doc">
        <div className="docs-detail-left">
          <section className="acard acard--pad">
            <h2 className="acard__title">
              reeve found {fixes.length} {fixes.length === 1 ? 'thing' : 'things'} to fix
            </h2>
            <DocumentFixList
              fixes={fixes}
              locatedIds={locatedFixIds}
              activeId={activeFix}
              onActivate={setActiveFix}
              onJump={(id) => fixHandle.current?.jumpTo(id)}
              onApply={(id) => fixHandle.current?.applyFix(id)}
              onReopen={(id) => fixHandle.current?.reopen(id)}
            />
          </section>
        </div>

        <div className="docs-detail-right">
          <div className="docs-viewer">
            <div className="docs-viewer__bar">
              <span className="docs-viewer__file">sample-policy.docx</span>
            </div>
            <DocxEditorPane
              ref={fixHandle}
              documentBuffer={buffer}
              fixes={fixes}
              activeFixId={activeFix}
              onActiveFixChange={setActiveFix}
              onLocatedChange={(ids) => setLocatedFixIds(new Set(ids))}
              onFixApplied={(id) => setFixes((prev) => prev.filter((f) => f.id !== id))}
              fallback={<div className="docs-viewer__sheet docs-viewer__sheet--empty">Loading sample…</div>}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
